'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'
import { Plus } from 'lucide-react'
import {
  TOURNAMENT_GAMES,
  gameIcon,
  filterByTime,
  gamerPlugFirst,
  type TimeFilter,
  type Tournament,
} from '@/lib/tournaments'
import { useLocalTimezone } from '@/lib/use-local-timezone'
import { EmptyState } from '@/components/tournaments/shared'
import { TournamentFilters } from '@/components/tournaments/filters'
import { TournamentCard } from '@/components/tournaments/tournament-card'
import {
  getTournamentCardSummaries,
  type TournamentCardSummary,
} from '@/lib/tournament-registration'

/** Per-game hero banner art + blurb. Games without art fall back to a gradient. */
const HERO_IMAGES: Record<string, string> = {
  'apex-legends': '/tournaments/apex-hero.jpg',
  valorant: '/tournaments/valorant-hero.jpg',
  'league-of-legends': '/tournaments/league-of-legends-hero.jpg',
  fortnite: '/tournaments/fortnite-hero.jpg',
}
const HERO_BLURBS: Record<string, string> = {
  'apex-legends':
    'Drop in, squad up, and battle for the biggest prize pools in Apex Legends. Browse upcoming tournaments and lock in your spot.',
  valorant:
    'Lock in your agents and outgun the competition. Browse upcoming Valorant tournaments and claim your spot.',
  'league-of-legends':
    'Draft, push, and close out the Rift. Browse upcoming League of Legends tournaments and compete for the prize pool.',
  fortnite:
    'Build, fight, and outlast the lobby. Browse upcoming Fortnite tournaments and drop in to compete.',
}

// Past events accumulate indefinitely (the ingest functions no longer prune
// them), so the Past tab renders in chunks and grows as the sentinel scrolls
// into view. Everything is already in memory — `getAllTournaments` hands the
// whole list down as a prop — so this caps DOM nodes, it does not fetch.
const PAST_PAGE_SIZE = 20

export function TournamentsClient({
  tournaments,
  locale,
}: {
  tournaments: Tournament[]
  locale: string
}) {
  const searchParams = useSearchParams()
  const [gameSlug, setGameSlug] = useState(() => {
    const param = searchParams.get('game')
    return TOURNAMENT_GAMES.some((g) => g.slug === param) ? param! : 'apex-legends'
  })
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('upcoming')

  // Render the timeline in the viewer's local timezone so the day label,
  // weekday, and time all agree with the user's clock (ingested events are
  // stored as UTC). Undefined until after mount; falls back to each event's
  // own stored zone in the meantime.
  const localTz = useLocalTimezone()

  // One batched fetch for all card summaries (who's registered, etc.),
  // keyed by tournament id, instead of one request per card.
  const [summaries, setSummaries] = useState<Map<string, TournamentCardSummary>>(
    () => new Map()
  )
  useEffect(() => {
    let cancelled = false
    getTournamentCardSummaries(tournaments.map((t) => t.id)).then((m) => {
      if (!cancelled) setSummaries(m)
    })
    return () => {
      cancelled = true
    }
  }, [tournaments])

  const gameTournaments = useMemo(
    () => tournaments.filter((t) => t.gameSlug === gameSlug),
    [tournaments, gameSlug]
  )

  // Upcoming / Past, sorted (same rule as the app's tournaments page).
  // Upcoming leads with GamerPlug's own events (stable sort keeps time order),
  // exactly like the app's Tournaments section.
  const filtered = useMemo(() => {
    const list = filterByTime(gameTournaments, timeFilter)
    return timeFilter === 'upcoming' ? list.slice().sort(gamerPlugFirst) : list
  }, [gameTournaments, timeFilter])

  // How many Past events are currently rendered. Upcoming is never capped —
  // it is bounded by definition and users expect the whole schedule at once.
  const [visibleCount, setVisibleCount] = useState(PAST_PAGE_SIZE)

  // Reset the window whenever the list identity changes, so switching game or
  // tab never leaves a stale (too large or too small) count behind.
  useEffect(() => {
    setVisibleCount(PAST_PAGE_SIZE)
  }, [timeFilter, gameSlug])

  const isPaged = timeFilter === 'past'
  const visible = useMemo(
    () => (isPaged ? filtered.slice(0, visibleCount) : filtered),
    [filtered, isPaged, visibleCount]
  )
  const hasMore = isPaged && visibleCount < filtered.length

  // Sentinel below the list: when it scrolls into view, reveal the next chunk.
  // A callback ref (rather than useRef + useEffect) re-attaches the observer
  // whenever the node mounts or unmounts — the sentinel is conditional, so it
  // comes and goes as `hasMore` flips.
  const observerRef = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect()
      if (!node) return
      // No IntersectionObserver (old browser, jsdom): the Load more button
      // below is the fallback, so paging still works without this.
      if (typeof IntersectionObserver === 'undefined') return
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            setVisibleCount((c) => c + PAST_PAGE_SIZE)
          }
        },
        // Start loading before the sentinel is actually on screen so the next
        // chunk is usually in place by the time the user reaches it.
        { rootMargin: '400px' }
      )
      observerRef.current.observe(node)
    },
    []
  )

  // Disconnect on unmount — the callback ref only fires while mounted.
  useEffect(() => () => observerRef.current?.disconnect(), [])

  const gameName =
    TOURNAMENT_GAMES.find((g) => g.slug === gameSlug)?.name ?? 'Game'

  const heroImage = HERO_IMAGES[gameSlug]
  const heroBlurb =
    HERO_BLURBS[gameSlug] ??
    `Browse upcoming ${gameName} tournaments — squad up, compete, and climb for the prize pool.`

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero banner */}
      <section className="relative isolate overflow-hidden">
        {heroImage ? (
          <Image
            src={heroImage}
            alt=""
            fill
            priority
            quality={90}
            sizes="100vw"
            className="-z-10 object-cover object-[70%_center]"
          />
        ) : (
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/40 via-primary/10 to-background" />
        )}
        {/* readability scrims */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-background via-background/75 to-background/20" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-transparent to-transparent" />

        <div className="container mx-auto max-w-5xl px-6 pb-12 pt-32">
          <Image
            src={gameIcon(gameName)}
            alt=""
            width={48}
            height={48}
            unoptimized
            className="mb-5 size-12 rounded-xl"
          />
          <p className="text-sm font-medium text-white/70">What&apos;s happening in</p>
          <h1 className="mt-1 text-5xl font-bold tracking-tight text-white drop-shadow-sm sm:text-6xl">
            {gameName}
          </h1>
          <p className="mt-5 max-w-xl leading-relaxed text-white/85">
            {heroBlurb}
          </p>
        </div>
      </section>

      <div className="relative container mx-auto max-w-5xl px-6 pt-10 pb-20">
        {/* Game pills — single scrollable row on mobile, wraps on larger screens */}
        <TournamentFilters
          game={gameSlug}
          onGameChange={setGameSlug}
          time={timeFilter}
          onTimeChange={setTimeFilter}
          pillId="tournaments-time-pill"
        />

        {/* One grid of cards, soonest first (each card shows its own date) */}
        <div className="mt-10">
          {visible.length === 0 ? (
            // Games we've just opened up have no events yet, so the empty state
            // doubles as the prompt to host the first one.
            <EmptyState
              variant={timeFilter === 'past' ? 'no-results' : 'no-tournaments'}
              action={
                timeFilter === 'upcoming' ? (
                  <Link
                    href={`/${locale}/tournaments/create`}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                  >
                    <Plus className="size-4" />
                    Host a {gameName} tournament
                  </Link>
                ) : undefined
              }
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((t) => (
                <TournamentCard
                  key={t.id}
                  t={t}
                  locale={locale}
                  displayTz={localTz}
                  showRegister={timeFilter === 'upcoming'}
                  registered={summaries.get(t.id)?.isRegistered ?? false}
                />
              ))}
            </div>
          )}
        </div>

        {/* Infinite-scroll sentinel + explicit fallback. The button matters for
            keyboard and screen-reader users, who never scroll the sentinel into
            view, and covers browsers without IntersectionObserver. */}
        {hasMore && (
          <div ref={sentinelRef} className="mt-8 flex justify-center">
            <button
              onClick={() => setVisibleCount((c) => c + PAST_PAGE_SIZE)}
              className="rounded-xl border border-border bg-card/60 px-5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Load more
            </button>
          </div>
        )}

        {visible.length > 0 && (
          <p className="mt-2 text-center text-xs text-muted-foreground" aria-live="polite">
            {hasMore
              ? `Showing ${visible.length} of ${filtered.length} ${gameName} events`
              : `${filtered.length} ${gameName} ${filtered.length === 1 ? 'event' : 'events'}`}
          </p>
        )}
      </div>

      {/* Sticky create-event CTA — bottom right on every viewport */}
      <Link
        href={`/${locale}/tournaments/create`}
        className="group fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-primary py-3.5 pl-4 pr-5 font-semibold text-white shadow-lg shadow-primary/30 ring-1 ring-white/10 transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/40"
        aria-label="Create event"
      >
        <Plus className="size-5 shrink-0" />
        <span className="text-sm">Create event</span>
      </Link>
    </div>
  )
}

